// Mesure du rythme de vente des places (vélocité) à partir de relevés horodatés.
// Logique pure ici ; la persistance localStorage est ajoutée plus bas.

export interface Snapshot {
  ts: number;        // epoch ms du relevé
  seatsSold: number; // places vendues à cet instant
}

export type VelocityLevel = 'stalled' | 'calm' | 'moving' | 'fast';

export interface VelocityReading {
  deltaSeats: number;     // places vendues sur la fenêtre observée
  windowMinutes: number;  // durée de la fenêtre, arrondie (≥ 1)
  ratePerMin: number;     // places / minute
  level: VelocityLevel;
}

// --- Constantes ajustables ---
export const WINDOW_MS = 15 * 60 * 1000;     // rétention + fenêtre max de comparaison
export const MIN_WINDOW_MS = 3 * 60 * 1000;  // recul minimal pour afficher un rythme
const RATE_MOVING = 0.3;                     // places/min : seuil bas "ça bouge"
const RATE_FAST = 0.8;                       // places/min : seuil bas "ça part vite"

export function classifyRate(deltaSeats: number, ratePerMin: number): VelocityLevel {
  if (deltaSeats <= 0) return 'stalled';
  if (ratePerMin >= RATE_FAST) return 'fast';
  if (ratePerMin >= RATE_MOVING) return 'moving';
  return 'calm';
}

export function pruneSnapshots(snapshots: Snapshot[], now: number): Snapshot[] {
  return snapshots.filter((s) => s.ts <= now && now - s.ts <= WINDOW_MS);
}

export function computeVelocity(snapshots: Snapshot[], now: number): VelocityReading | null {
  const inWindow = pruneSnapshots(snapshots, now).slice().sort((a, b) => a.ts - b.ts);
  if (inWindow.length < 2) return null;

  const oldest = inWindow[0];
  const newest = inWindow[inWindow.length - 1];
  const spanMs = newest.ts - oldest.ts;
  if (spanMs < MIN_WINDOW_MS) return null;

  const deltaSeats = newest.seatsSold - oldest.seatsSold;
  if (deltaSeats < 0) return null; // remboursement/annulation : pas de rythme négatif

  const spanMin = spanMs / 60000;
  const ratePerMin = deltaSeats / spanMin;

  return {
    deltaSeats,
    // spanMin >= MIN_WINDOW_MS/60000 = 3 ici ; le max(1, …) protège seulement
    // le cas où MIN_WINDOW_MS serait un jour abaissé sous 1 min.
    windowMinutes: Math.max(1, Math.round(spanMin)),
    ratePerMin,
    level: classifyRate(deltaSeats, ratePerMin),
  };
}

// --- Persistance localStorage ---
const STORAGE_KEY = 'idanroh-velocity-snapshots';
type SnapshotStore = Record<string, Snapshot[]>;

function loadStore(): SnapshotStore {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as SnapshotStore) : {};
  } catch {
    return {};
  }
}

function saveStore(store: SnapshotStore): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(store));
  } catch {
    /* quota dépassé / localStorage indisponible — on ignore */
  }
}

export function recordSnapshot(departureId: string, seatsSold: number, now: number = Date.now()): void {
  const store = loadStore();
  store[departureId] = [...(store[departureId] ?? []), { ts: now, seatsSold }];
  // Élaguer chaque départ et supprimer les clés vides pour borner le stockage.
  for (const id of Object.keys(store)) {
    const pruned = pruneSnapshots(store[id], now);
    if (pruned.length > 0) store[id] = pruned;
    else delete store[id];
  }
  saveStore(store);
}

export function getVelocity(departureId: string, now: number = Date.now()): VelocityReading | null {
  const store = loadStore();
  return computeVelocity(store[departureId] ?? [], now);
}
